import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useProperties, PropertyCard, PropertyFilters } from '@features/properties';
import { Skeleton } from "@shared/components/ui/skeleton";
import { Button } from "@shared/components/ui/button";
import { Building2, MapPin, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import type { Property, PropertyFilters as PropertyFiltersType } from '@/types';
import type { UIFilters } from '../types';

/**
 * Convert UI filter state to API filter format
 */
function toApiFilters(uiFilters: UIFilters): PropertyFiltersType {
  const apiFilters: PropertyFiltersType = {};

  if (uiFilters.search && uiFilters.search.trim()) {
    apiFilters.search = uiFilters.search.trim();
  }
  if (uiFilters.district && uiFilters.district !== 'all') {
    apiFilters.district = uiFilters.district;
  }
  if (uiFilters.propertyType && uiFilters.propertyType !== 'all') {
    apiFilters.propertyType = uiFilters.propertyType;
  }
  if (uiFilters.bedrooms && uiFilters.bedrooms !== 'all') {
    const bedroomNum = parseInt(uiFilters.bedrooms, 10);
    if (uiFilters.bedrooms === '5') {
      apiFilters.minBedrooms = 5;
    } else {
      apiFilters.minBedrooms = bedroomNum;
      apiFilters.maxBedrooms = bedroomNum;
    }
  }
  if (uiFilters.priceRange) {
    apiFilters.minPrice = uiFilters.priceRange[0];
    apiFilters.maxPrice = uiFilters.priceRange[1];
  }

  return apiFilters;
}

/**
 * Client-side filter for fallback when server doesn't filter
 */
function filterProperties(properties: Property[], filters: UIFilters): Property[] {
  return properties.filter((property) => {
    // Search match - check title and district label
    const searchMatch = !filters.search ||
      property.title?.toLowerCase().includes(filters.search.toLowerCase()) ||
      property.district?.label?.toLowerCase().includes(filters.search.toLowerCase());

    // District match - compare by code
    const districtMatch = filters.district === 'all' ||
      property.district?.code === filters.district;

    // Type match - compare by code
    const typeMatch = filters.propertyType === 'all' ||
      property.propertyType.code === filters.propertyType;

    // Bedroom match
    const bedroomMatch = filters.bedrooms === 'all' || (
      filters.bedrooms === '5'
        ? property.bedrooms >= 5
        : property.bedrooms === parseInt(filters.bedrooms)
    );

    // Price match
    const priceMatch = property.price >= filters.priceRange[0] &&
      property.price <= filters.priceRange[1];

    return searchMatch && districtMatch && typeMatch && bedroomMatch && priceMatch;
  });
}

export default function PropertiesList() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<UIFilters>({
    search: '',
    district: 'all',
    propertyType: 'all',
    bedrooms: 'all',
    priceRange: [500000, 10000000]
  });

  // Pass filters to API for server-side filtering
  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);
  const { data: propertiesData, isLoading } = useProperties(apiFilters);
  const properties = propertiesData?.data || [];

  // Client-side filtering as additional safety
  // When backend fully implements all filters, this becomes a no-op
  const filteredProperties = useMemo(() =>
    filterProperties(properties, filters),
    [properties, filters]
  );

  const resetFilters = () => {
    setFilters({
      search: '',
      district: 'all',
      propertyType: 'all',
      bedrooms: 'all',
      priceRange: [500000, 10000000]
    });
  };

  const stats = [
    { icon: Building2, value: properties.length, label: t('stats.availableProperties') },
    { icon: MapPin, value: '10+', label: t('stats.districtsInRiyadh') },
    { icon: TrendingUp, value: '100%', label: t('stats.digital') }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900/90" />

        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-relaxed">
              {t('hero.title')}
              <br />
              <br />
              <span className="text-emerald-400">{t('hero.subtitle')}</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {t('hero.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center gap-8 md:gap-16"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="lg:sticky lg:top-4">
              <PropertyFilters
                filters={filters}
                setFilters={setFilters}
                onReset={resetFilters}
              />
            </div>
          </div>

          {/* Properties Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {t('properties.available')}
                <span className="text-slate-400 font-normal mr-2">
                  ({filteredProperties.length})
                </span>
              </h2>
            </div>

            {isLoading ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden">
                    <Skeleton className="aspect-[4/3]" />
                    <div className="p-5 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-10 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProperties.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProperties.map((property, index) => (
                  <PropertyCard key={property.id} property={property} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl">
                <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('properties.noProperties')}</h3>
                <p className="text-slate-500 mb-4">{t('properties.tryDifferentFilters')}</p>
                <Button variant="outline" onClick={resetFilters}>
                  {t('properties.resetFilters')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
